import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { FormikProps } from "formik";
import { TripFormValues, Video } from "../types/tripTypes";

interface TripVideoDetailsProps {
  formik: FormikProps<TripFormValues>;
  editable?: boolean;
}

const TripVideoDetails: React.FC<TripVideoDetailsProps> = ({ 
  formik, 
  editable = true 
}) => {
  const { values, setFieldValue } = formik;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [urlErrors, setUrlErrors] = useState<{ [key: number]: string }>({});

  const MAX_VIDEOS = 3;

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // YouTube-only URL validation function
   const validateVideoUrl = (url: string): { isValid: boolean; error?: string } => {
    if (!url.trim()) {
      return { isValid: false, error: "YouTube URL is required" };
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?.*v=|embed\/|v\/)|youtu\.be\/)[\w-]+([&?][\w=&.-]*)*$/;

    if (youtubeRegex.test(url)) {
      return { isValid: true };
    } else {
      return { 
        isValid: false, 
        error: "Please enter a valid YouTube URL" 
      };
    }
  };

  const handleUrlChange = (index: number, url: string) => {
    const validation = validateVideoUrl(url);
    
    // Update error state
    setUrlErrors(prev => ({
      ...prev,
      [index]: validation.error || ""
    }));

    // Update video data
    const updatedVideos = [...values.youtube];
    updatedVideos[index] = {
      ...updatedVideos[index],
      preview: url,
      mimetype: "youtube", 
    };
    setFieldValue("youtube", updatedVideos);
  };

  const addVideo = () => {
    if (values.youtube.length >= MAX_VIDEOS) return;

    const newVideo: Video = {
      fileName: String(values.youtube.length + 1).padStart(3, "0"),
      mimetype: "youtube",
      preview: "",
    };
    setFieldValue("youtube", [...values.youtube, newVideo]);
  };

  const removeVideo = (index: number) => {
    const updatedVideos = values.youtube.filter((_, i) => i !== index);
    // Reorder file names
    const reorderedVideos = updatedVideos.map((video, i) => ({
      ...video,
      fileName: String(i + 1).padStart(3, "0"),
    }));
    setFieldValue("youtube", reorderedVideos);
    
    if (expandedIndex === index) setExpandedIndex(null);
    
    // Clear error for removed video
    setUrlErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const getVideoThumbnail = (url: string) => {
    if (!url) return null;
    
    try {
      // Extract video ID from various YouTube URL formats
      let videoId = null;
      
      // For youtube.com/watch URLs (with or without additional parameters)
      const watchMatch = url.match(/[?&]v=([^&\n?#]+)/);
      if (watchMatch) {
        videoId = watchMatch[1];
      }
      
      // For youtu.be URLs
      const shortMatch = url.match(/youtu\.be\/([^&\n?#]+)/);
      if (shortMatch) {
        videoId = shortMatch[1];
      }
      
      // For embed URLs
      const embedMatch = url.match(/youtube\.com\/embed\/([^&\n?#]+)/);
      if (embedMatch) {
        videoId = embedMatch[1];
      }
      
      return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
    } catch (error) {
      return null;
    }
  };

  return (
    <Card sx={{ marginTop: 3, padding: 2, width: "100%", marginX: "auto" }}>
      <CardContent>
        <Typography
          fontWeight={600}
          variant="h6"
          color="#181D27"
          marginBottom={1}
        >
          Video Details
        </Typography>
        <Typography variant="body2" color="#535862" marginBottom={1}>
          Add up to {MAX_VIDEOS} YouTube videos to showcase your trip.
        </Typography>
        
        {values.youtube.length >= MAX_VIDEOS && (
          <Alert severity="info" sx={{ marginBottom: 2, fontSize: "14px" }}>
            Maximum {MAX_VIDEOS} videos allowed. Remove a video to add a new one.
          </Alert>
        )}

        {values.youtube.map((video, index) => (
          <Accordion
            key={video.fileName}
            expanded={expandedIndex === index}
            onChange={() => handleExpand(index)}
            sx={{
              marginY: 2,
              "&:before": { display: "none" },
              border: urlErrors[index] ? "1px solid #f44336" : "1px solid #D5D7DA",
              borderRadius: "8px !important",
              boxShadow: "none",
              "&.Mui-expanded": {
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon 
                  sx={{ 
                    flexShrink: 0 
                  }} 
                />
              }
              sx={{ 
                margin: 0, 
                minHeight: "56px",
                "& .MuiAccordionSummary-content": {
                  minWidth: 0, 
                  overflow: "hidden" 
                }
              }}
            >
              <Box
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  width: "100%", 
                  minWidth: 0, 
                  gap: 1 
                }}
              >
                <PlayArrowIcon 
                  sx={{ 
                    color: "#FF0000",
                    fontSize: "20px",
                    flexShrink: 0
                  }} 
                />
                <Typography
                  variant="body1"
                  color="#181D27"
                  fontWeight={600}
                  fontSize="18px"
                  sx={{ flexShrink: 0 }}
                >
                  YouTube Video {index + 1}
                </Typography>

                {/* YouTube Chip */}
                <Chip
                  label="YOUTUBE"
                  size="small"
                  sx={{
                    backgroundColor: "#FF0000",
                    color: "white",
                    fontSize: "10px",
                    height: "20px",
                    flexShrink: 0
                  }}
                />

                {/* Show URL preview when collapsed */}
                {video.preview && expandedIndex !== index && (
                  <Typography
                    variant="body2"
                    color="#535862"
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: "0.01em",
                      fontWeight: "400",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginLeft: 1,
                      flex: 1,
                      minWidth: 0, 
                    }}
                  >
                    {video.preview}
                  </Typography>
                )}

                {/* Error indicator */}
                {urlErrors[index] && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ 
                      marginLeft: 1, 
                      fontSize: "12px",
                      flexShrink: 0,
                      whiteSpace: "nowrap"
                    }}
                  >
                    Invalid URL
                  </Typography>
                )}

                <IconButton
                  aria-label="delete"
                  size="small"
                  disabled={!editable}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeVideo(index);
                  }}
                  sx={{ 
                    marginLeft: "auto",
                    flexShrink: 0 
                  }}
                >
                  <DeleteIcon color={editable ? "error" : "disabled"} />
                </IconButton>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: 2 }}>
              {/* Video URL Input */}
              <Box sx={{ marginTop: "10px" }}>
                <Typography
                  variant="h6"
                  my={1}
                  sx={{
                    color: "#414651",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    textAlign: "left",
                  }}
                >
                  YouTube URL *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={video.preview}
                  disabled={!editable}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=example or https://youtu.be/example"
                  error={!!urlErrors[index]}
                  helperText={urlErrors[index] || "Paste a valid YouTube URL (youtube.com or youtu.be)"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-error": {
                        "& fieldset": {
                          borderColor: "#f44336",
                        },
                      },
                    },
                  }}
                />
              </Box>

              {/* Video Preview */}
              {video.preview && !urlErrors[index] && (
                <Box sx={{ marginTop: "20px" }}>
                  <Typography
                    variant="h6"
                    my={1}
                    sx={{
                      color: "#414651",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "20px",
                      textAlign: "left",
                    }}
                  >
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #D5D7DA",
                      borderRadius: "8px",
                      padding: 2,
                      backgroundColor: "#f9f9f9",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {getVideoThumbnail(video.preview) && (
                      <img
                        src={getVideoThumbnail(video.preview)!}
                        alt="YouTube video thumbnail"
                        style={{
                          width: "80px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="#181D27"
                        sx={{ marginBottom: "4px" }}
                      >
                        YouTube Video
                      </Typography>
                      <Typography
                        variant="caption"
                        color="#535862"
                        sx={{
                          wordBreak: "break-all",
                          display: "block",
                          maxWidth: "300px",
                        }}
                      >
                        {video.preview}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* File Info */}
              <Box sx={{ marginTop: "20px" }}>
                <Typography
                  variant="caption"
                  color="#535862"
                  sx={{ fontStyle: "italic" }}
                >
                  File Name: {video.fileName} | Type: YouTube
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Add New Video Button */}
        {editable && values.youtube.length < MAX_VIDEOS && (
          <Box
            onClick={addVideo}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#414651",
              fontWeight: 500,
              marginTop: "20px",
              border: "1px solid #D5D7DA",
              borderRadius: "10px",
              width: "200px",
              padding: "10px",
              height: "40px",
              fontFamily: "Inter, sans-serif",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": { 
                backgroundColor: "#f1f1f1",
                borderColor: "#B0B3B8",
                transform: "translateY(-1px)",
              },
            }}
          >
            Add Details
          </Box>
        )}

        {/* Videos Summary */}
        {values.youtube.length > 0 && (
          <Box sx={{ marginTop: 2, padding: 1, backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
            <Typography variant="caption" color="#535862">
              {values.youtube.length} of {MAX_VIDEOS} YouTube videos added
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TripVideoDetails;